import { parseMoneyAmount } from 'utils/moneyInput'

export const LOW_RENT_WARNING_THRESHOLD = 300
export const MONTHLY_RENT_MIN = 1
export const MONTHLY_RENT_MAX = 50_000
export const AREA_MIN_SQFT = 100
export const AREA_MAX_SQFT = 10_000
export const TRANSFER_INCENTIVE_MAX = 10_000
export const PETS_CONDITIONS_MAX = 200
export const PARKING_EXTRA_NOTE_MAX = 200
export const ELECTROMENAGERS_PAID_NOTE_MAX = 200
export const DATE_RANGE_MONTHS = 24

export type DateFieldKey = 'desiredMoveInDate' | 'availabilityDate' | 'contractEndDate'

export function digitsOnlyInteger(value: string): string {
  return value.replace(/\D/g, '')
}

export function todayIsoDate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function addMonthsIso(isoDate: string, months: number): string {
  const [y, m, day] = isoDate.split('-').map(Number)
  const d = new Date(y, m - 1 + months, day)
  const ny = d.getFullYear()
  const nm = String(d.getMonth() + 1).padStart(2, '0')
  const nd = String(d.getDate()).padStart(2, '0')
  return `${ny}-${nm}-${nd}`
}

export function maxSelectableDate(): string {
  return addMonthsIso(todayIsoDate(), DATE_RANGE_MONTHS)
}

export function isValidMonthlyRent(value: string): boolean {
  const amount = parseMoneyAmount(value)
  if (amount === null) return false
  return amount >= MONTHLY_RENT_MIN && amount <= MONTHLY_RENT_MAX
}

export function isLowMonthlyRentWarning(value: string): boolean {
  const amount = parseMoneyAmount(value)
  return amount !== null && amount > 0 && amount < LOW_RENT_WARNING_THRESHOLD
}

export function isValidAreaSqFt(value: string): boolean {
  const raw = digitsOnlyInteger(value)
  if (!raw) return true
  const n = Number.parseInt(raw, 10)
  if (Number.isNaN(n)) return false
  return n >= AREA_MIN_SQFT && n <= AREA_MAX_SQFT
}

export function isValidTransferIncentive(value: string): boolean {
  const amount = parseMoneyAmount(value)
  if (amount === null) return true
  return amount >= 0 && amount <= TRANSFER_INCENTIVE_MAX
}

export function capTransferIncentive(value: string): string {
  const amount = parseMoneyAmount(value)
  if (amount === null) return ''
  return String(Math.min(amount, TRANSFER_INCENTIVE_MAX))
}

function compareIsoDates(a: string, b: string): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

export function validateDesiredMoveInDate(value: string): DateFieldKey | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const today = todayIsoDate()
  const max = maxSelectableDate()
  if (compareIsoDates(trimmed, today) < 0) return 'desiredMoveInDate'
  if (compareIsoDates(trimmed, max) > 0) return 'desiredMoveInDate'
  return null
}

export function validateAvailabilityDate(value: string): DateFieldKey | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const today = todayIsoDate()
  const max = maxSelectableDate()
  if (compareIsoDates(trimmed, today) < 0) return 'availabilityDate'
  if (compareIsoDates(trimmed, max) > 0) return 'availabilityDate'
  return null
}

export function validateContractEndDate(
  value: string,
  availabilityDate: string,
  desiredMoveInDate: string
): DateFieldKey | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const today = todayIsoDate()
  if (compareIsoDates(trimmed, today) < 0) return 'contractEndDate'
  if (availabilityDate.trim() && compareIsoDates(trimmed, availabilityDate.trim()) < 0) {
    return 'contractEndDate'
  }
  if (desiredMoveInDate.trim() && compareIsoDates(trimmed, desiredMoveInDate.trim()) < 0) {
    return 'contractEndDate'
  }
  return null
}

export function areDescriptionDatesValid(form: {
  desiredMoveInDate: string
  contractEndDate: string
  availabilityDate: string
}): boolean {
  return (
    validateDesiredMoveInDate(form.desiredMoveInDate) === null &&
    validateAvailabilityDate(form.availabilityDate) === null &&
    validateContractEndDate(
      form.contractEndDate,
      form.availabilityDate,
      form.desiredMoveInDate
    ) === null
  )
}
